import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import axios from "axios";
import * as XLSX from "xlsx";

import {
  FaArrowLeft,
  FaArrowRight,
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaFileExcel,
  FaMoneyBillWave,
  FaUsers,
} from "react-icons/fa";

import "./MonthlyDueClassPage.css";


const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://dgps.co.in";


const MonthlyDueClassPage = () => {

  const { month } = useParams();

  const navigate = useNavigate();

  const location = useLocation();


  const [classes, setClasses] =
    useState([]);

  const [monthName, setMonthName] =
    useState(
      location.state?.monthName ||
      month
    );

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");


  /* ============================================
     HEADER
  ============================================ */

  const getHeaders = () => {

    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("auth_token") ||
      localStorage.getItem("access_token") ||
      "";


    const schoolId =
      localStorage.getItem("school_id") ||
      localStorage.getItem("schoolId") ||
      localStorage.getItem(
        "selected_school_id"
      ) ||
      "";


    return {

      Authorization: token
        ? `Bearer ${token}`
        : "",

      "X-School-Id": schoolId,

      Accept: "application/json",
    };
  };


  /* ============================================
     MONEY
  ============================================ */

  const money = (amount) => {

    return new Intl.NumberFormat(
      "en-IN",
      {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }
    ).format(
      Number(amount || 0)
    );
  };


  /* ============================================
     LOAD CLASS REPORT
  ============================================ */

  const loadClasses = async () => {

    try {

      setLoading(true);

      setError("");


      const response =
        await axios.get(

          `${API_BASE_URL}/api/reports/fee-due/class-summary`,

          {
            params: {
              month: month,
            },

            headers: getHeaders(),
          }
        );


      console.log(
        "CLASS SUMMARY RESPONSE:",
        response.data
      );


      if (response?.data?.status) {

        const data =
          response.data.data || {};


        setClasses(
          Array.isArray(data.classes)
            ? data.classes
            : []
        );


        setMonthName(
          data.month_name ||
          location.state?.monthName ||
          month
        );

      } else {

        setClasses([]);

        setError(
          response?.data?.message ||
          "Unable to load class report."
        );
      }

    } catch (err) {

      console.error(
        "Class due report error:",
        err
      );


      setClasses([]);


      setError(
        err?.response?.data?.message ||
        "Unable to load class report."
      );

    } finally {

      setLoading(false);
    }
  };


  useEffect(() => {

    loadClasses();

  }, [month]);


  /* ============================================
     DUE CLASSES

     IMPORTANT:
     OLD:
     total_due_until_month ❌

     NEW:
     month_due_amount ✅
  ============================================ */

  const dueClasses =
    useMemo(() => {

      return classes.filter(
        (row) =>
          Number(
            row.month_due_amount || 0
          ) > 0
      );

    }, [classes]);


  /* ============================================
     TOTALS
  ============================================ */

  const totals =
    useMemo(() => {

      return classes.reduce(
        (result, row) => {

          result.totalStudents +=
            Number(
              row.student_count || 0
            );


          result.dueStudents +=
            Number(
              row.due_students ??
              row.month_due_students ??
              0
            );


          result.payable +=
            Number(
              row.month_payable || 0
            );


          result.collected +=
            Number(
              row.month_paid || 0
            );


          result.due +=
            Number(
              row.month_due_amount || 0
            );


          return result;
        },
        {
          totalStudents: 0,
          dueStudents: 0,
          payable: 0,
          collected: 0,
          due: 0,
        }
      );

    }, [classes]);


  /* ============================================
     VIEW STUDENT
  ============================================ */

  const viewStudents = (row) => {

    navigate(

      `/admin/reports/monthly-due/${month}/class/${row.class_id}`,

      {
        state: {

          monthName:
            monthName,

          className:
            row.class_name,

          classId:
            row.class_id,

          monthPayable:
            row.month_payable,

          monthCollected:
            row.month_paid,

          monthDue:
            row.month_due_amount,

          monthDueStudents:
            row.due_students ??
            row.month_due_students ??
            0,
        },
      }
    );
  };


  /* ============================================
     EXCEL
  ============================================ */

  const exportExcel = () => {

    if (!classes.length) {
      return;
    }


    const rows =
      classes.map(
        (row, index) => ({

          "#": index + 1,

          "Class":
            row.class_name || "",

          "Total Students":
            Number(
              row.student_count || 0
            ),

          "Due Students":
            Number(
              row.due_students ??
              row.month_due_students ??
              0
            ),

          "Total Payable":
            Number(
              row.month_payable || 0
            ),

          "Total Collected":
            Number(
              row.month_paid || 0
            ),

          "Due Amount":
            Number(
              row.month_due_amount || 0
            ),
        })
      );


    rows.push({

      "#": "",

      "Class":
        "GRAND TOTAL",

      "Total Students":
        totals.totalStudents,

      "Due Students":
        totals.dueStudents,

      "Total Payable":
        totals.payable,

      "Total Collected":
        totals.collected,

      "Due Amount":
        totals.due,
    });


    const worksheet =
      XLSX.utils.json_to_sheet(
        rows
      );


    worksheet["!cols"] = [

      { wch: 6 },

      { wch: 20 },

      { wch: 16 },

      { wch: 16 },

      { wch: 18 },

      { wch: 18 },

      { wch: 18 },
    ];


    const workbook =
      XLSX.utils.book_new();


    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Class Due"
    );


    XLSX.writeFile(
      workbook,
      `Class_Due_${month}.xlsx`
    );
  };


  return (

    <div className="due-class-page">


      {/* BACK */}

      <button
        type="button"
        className="due-class-back"
        onClick={() =>
          navigate(
            "/admin/reports/monthly-due"
          )
        }
      >

        <FaArrowLeft />

        Monthly Report

      </button>


      {/* HEADER */}

      <div className="due-class-header">


        <div className="due-class-header-icon">

          <FaCalendarAlt />

        </div>


        <div>

          <span>
            CLASS WISE MONTH REPORT
          </span>


          <h1>
            {monthName}
          </h1>


          <p>
            Only {monthName} payable,
            collection and due.
            Previous month due is not included.
          </p>

        </div>


      </div>


      {/* SUMMARY */}

      <div className="due-class-summary">


        {/* DUE CLASSES */}

        <div className="due-summary-item">

          <div className="due-summary-icon blue">

            <FaChalkboardTeacher />

          </div>


          <div>

            <span>
              Due Classes
            </span>

            <strong>
              {dueClasses.length}
            </strong>

          </div>

        </div>


        {/* DUE STUDENTS */}

        <div className="due-summary-item">

          <div className="due-summary-icon purple">

            <FaUsers />

          </div>


          <div>

            <span>
              {monthName} Due Students
            </span>

            <strong>
              {totals.dueStudents}
            </strong>

          </div>

        </div>


        {/* COLLECTED */}

        <div className="due-summary-item">

          <div className="due-summary-icon orange">

            <FaMoneyBillWave />

          </div>


          <div>

            <span>
              {monthName} Collected
            </span>

            <strong
              style={{
                color: "#15803d",
              }}
            >
              {money(
                totals.collected
              )}
            </strong>

          </div>

        </div>


        {/* DUE */}

        <div className="due-summary-item">

          <div className="due-summary-icon red">

            <FaMoneyBillWave />

          </div>


          <div>

            <span>
              {monthName} Due
            </span>

            <strong className="summary-due-red">

              {money(
                totals.due
              )}

            </strong>

          </div>

        </div>


      </div>


      {/* ERROR */}

      {error && (

        <div className="due-class-error">

          {error}

        </div>

      )}


      {/* TABLE CARD */}

      <div className="due-class-table-card">


        <div className="due-class-table-header">


          <div>

            <h2>
              Class Wise Due List
            </h2>

            <p>
              Click View to see
              student-wise {monthName} due.
            </p>

          </div>


          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >


            <button
              type="button"
              onClick={exportExcel}
              disabled={
                loading ||
                !classes.length
              }
              style={{
                border: 0,
                outline: 0,
                padding:
                  "9px 15px",
                borderRadius:
                  "8px",
                background:
                  "#15803d",
                color:
                  "#ffffff",
                cursor:
                  "pointer",
                fontSize:
                  "11px",
                fontWeight:
                  "800",
                display:
                  "inline-flex",
                alignItems:
                  "center",
                gap:
                  "7px",
              }}
            >

              <FaFileExcel />

              Excel

            </button>


            <div className="due-class-total-box">

              <span>
                Month Outstanding
              </span>

              <strong>

                {money(
                  totals.due
                )}

              </strong>

            </div>


          </div>


        </div>


        {/* LOADING */}

        {loading ? (

          <div className="due-class-loading">

            <div className="due-class-loader" />

            Loading class report...

          </div>

        ) : (

          <div className="due-class-table-wrap">


            <table className="due-class-table">


              <thead>

                <tr>


                  <th className="serial-column">
                    #
                  </th>


                  <th>
                    Class
                  </th>


                  <th className="text-center">
                    Total Students
                  </th>


                  <th className="text-center">
                    Due Students
                  </th>


                  <th className="text-right">
                    Total Payable
                  </th>


                  <th className="text-right">
                    Total Collected
                  </th>


                  <th className="text-right">
                    Due Amount
                  </th>


                  <th className="text-center">
                    Action
                  </th>


                </tr>

              </thead>


              <tbody>


                {!classes.length ? (

                  <tr>

                    <td
                      colSpan="8"
                      className="due-class-empty"
                    >

                      No class data found.

                    </td>

                  </tr>

                ) : (

                  classes.map(
                    (
                      row,
                      index
                    ) => (

                      <tr
                        key={
                          row.class_id
                        }
                      >


                        {/* # */}

                        <td className="serial-column">

                          {index + 1}

                        </td>


                        {/* CLASS */}

                        <td>

                          <div className="class-name-column">

                            <div className="class-small-icon">

                              <FaChalkboardTeacher />

                            </div>


                            <strong>

                              {
                                row.class_name ||
                                "-"
                              }

                            </strong>

                          </div>

                        </td>


                        {/* TOTAL STUDENTS */}

                        <td className="text-center">

                          {
                            row.student_count ||
                            0
                          }

                        </td>


                        {/* DUE STUDENTS */}

                        <td className="text-center">

                          <span
                            className={
                              Number(
                                row.due_students ??
                                row.month_due_students ??
                                0
                              ) > 0

                                ? "due-student-count"

                                : "zero-student-count"
                            }
                          >

                            {
                              row.due_students ??
                              row.month_due_students ??
                              0
                            }

                          </span>

                        </td>


                        {/* TOTAL PAYABLE */}

                        <td className="text-right">

                          <strong
                            style={{
                              color:
                                "#102a43",
                            }}
                          >

                            {money(
                              row.month_payable
                            )}

                          </strong>

                        </td>


                        {/* TOTAL COLLECTED */}

                        <td className="text-right">

                          <strong
                            style={{
                              color:
                                "#15803d",
                            }}
                          >

                            {money(
                              row.month_paid
                            )}

                          </strong>

                        </td>


                        {/* DUE */}

                        <td className="text-right">

                          <strong
                            className={
                              Number(
                                row.month_due_amount ||
                                0
                              ) > 0

                                ? "total-due-value"

                                : "zero-due-value"
                            }
                          >

                            {money(
                              row.month_due_amount
                            )}

                          </strong>

                        </td>


                        {/* VIEW */}

                        <td className="text-center">

                          <button
                            type="button"
                            className="class-view-button"
                            onClick={() =>
                              viewStudents(
                                row
                              )
                            }
                          >

                            View

                            <FaArrowRight />

                          </button>

                        </td>


                      </tr>

                    )
                  )

                )}


              </tbody>


              {/* GRAND TOTAL */}

              {!!classes.length && (

                <tfoot>

                  <tr>


                    <td colSpan="2">

                      GRAND TOTAL

                    </td>


                    <td className="text-center">

                      {
                        totals.totalStudents
                      }

                    </td>


                    <td className="text-center">

                      {
                        totals.dueStudents
                      }

                    </td>


                    <td className="text-right">

                      {money(
                        totals.payable
                      )}

                    </td>


                    <td className="text-right">

                      {money(
                        totals.collected
                      )}

                    </td>


                    <td className="text-right">

                      {money(
                        totals.due
                      )}

                    </td>


                    <td />


                  </tr>

                </tfoot>

              )}


            </table>


          </div>

        )}


      </div>


    </div>
  );
};


export default MonthlyDueClassPage;